'use client';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import {
  IPagination, IPostInfo, IPostsListResponse,
} from '@sapientpro/sapientpro-data-models';
import { notFound, useSearchParams } from 'next/navigation';
import { useAtomValue } from 'jotai';
import styles from './ArticlesList.module.scss';
import ArticleCard from './ArticleCard';
import client from '../../../apiClient';
import { blogSearch } from '../../../store/blogFilters';
import { theme } from '../../../store/theme';
import Pagination from '../../../components/Pagination/Pagination';

interface IArticlesListProps {
  filters: {
    search?: string;
    category?: string;
  }
}

type GetArticlesParams = {
  search?: string;
  category?: string;
  limit: number;
  offset: number;
};

const BLOG_POSTS_LIMIT = 18;

const getArticles = async ({
  search,
  category,
  limit,
  offset,
}: GetArticlesParams) => {
  try {
    return await client.get<IPostsListResponse>(
      `posts?${new URLSearchParams(JSON.parse(JSON.stringify({
        limit,
        offset,
        us: false,
        ...(search ? { search } : {}),
        ...(category ? { category } : {}),
      })))}`,
      {
        cache: 'no-cache',
      },
    );
  } catch (e) {
    console.error(e.message);
    if (e.message.includes('404')) {
      notFound();
    }
  }
};

const ArticlesList = ({ filters }: IArticlesListProps) => {
  const params = useSearchParams();
  const initPage = Number(params.get('page') || 1) - 1;
  const appTheme = useAtomValue(theme);
  const search = useAtomValue(blogSearch);
  const [articles, setArticles] = useState<IPostInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paginationData, setPaginationData] = useState<IPagination>({
    limit: 18,
    offset: 0,
    total: 0,
    quantity: 0,
  });
  const listRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef<number>(0);

  const fetchArticles = async (newOffset?: number) => {
    try {
      const { limit } = paginationData;
      const hasOffset = typeof newOffset !== 'undefined';
      const offset = hasOffset ? newOffset : BLOG_POSTS_LIMIT * initPage;
      setIsLoading(true);
      const data = await getArticles({ ...filters, limit, offset });

      if (data) {
        setArticles(data?.posts);
        setPaginationData({ ...data.pagination, limit, offset });
      }
    } catch (error) {
      console.error('fetch articles error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOffsetChange = (offset: number) => {
    fetchArticles(offset);

    const page = offset / BLOG_POSTS_LIMIT + 1;
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set('page', page.toString());
    } else {
      url.searchParams.delete('page');
    }
    window.history.pushState(null, '', url.toString());

    const listRect = listRef.current?.getBoundingClientRect();

    window.scrollTo({ top: listRect?.top, behavior: 'smooth' });
  };

  useEffect(() => {
    fetchArticles();
  }, [filters]);

  useEffect(() => {
    const handlePopState = () => {
      const searchQueries = window.location.search.split('?')[1]?.split('&') || [];
      const pageQuery = searchQueries.find(query => query.startsWith('page=')) || '';
      const page = Number(pageQuery?.split('page=')?.[1] || 1) - 1;
      const newOffset = page * BLOG_POSTS_LIMIT;

      if (newOffset !== offsetRef.current) fetchArticles(newOffset);
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const showEmptyState = !isLoading && search && !articles.length;

  if (isLoading) {
    return (
      <section className={classNames(styles.loadingState, 'container')}>
        <img
          src={`/media/spinner_${appTheme}.webp`}
          alt='spinner'
          className={styles.loadingIcon}
        />
      </section>
    );
  }

  return (
    showEmptyState ? (
      <section className={classNames(styles.emptyState, 'container')}>
        <p className={styles.emptyStateText}>
          {`It looks like we don’t have any articles matching "${search}" right now.
           But we’re always adding new content! Let us know what you need - we’re happy to help.
            Just fill out the contact form below!`}
        </p>
      </section>
    ) : (
      <>
        <section
          className={classNames(styles.articlesList, 'container')}
          ref={listRef}
        >
          {articles.map(article => (
            <ArticleCard
              key={article.id}
              article={article}
            />
          ))}
        </section>
        <Pagination
          data={paginationData}
          handlePageChange={handleOffsetChange}
        />
      </>
    )
  );
};

export default ArticlesList;
